import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getGameStory,
  createGameStory,
  updateGameStory,
  generateStoryQRCode
} from '../../utils/api';
import { QRCodeSVG } from 'qrcode.react';
import LoadingState from '../../components/LoadingState';
import { v4 as uuidv4 } from 'uuid';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const QUESTION_TYPES = {
  MULTIPLE_CHOICE: 'multiple_choice',
  FILE_UPLOAD: 'file_upload',
  NO_ANSWER: 'no_answer',
  TEXT: 'text'
};

// Labels for question types in Japanese
const QUESTION_TYPE_LABELS = {
  multiple_choice: '選択問題',
  file_upload: 'ファイルアップロード',
  no_answer: '情報表示のみ',
  text: 'テキスト入力'
};

const GameEditor = () => {
  const { storyId } = useParams();
  const [activeTab, setActiveTab] = useState('details');
  const [story, setStory] = useState({
    storyId: uuidv4(),
    title: '',
    description: '',
    coverImageUrl: '',
    questionsAnswers: [],
    isSequential: true,
  });
  
  const [newQuestion, setNewQuestion] = useState({
    questionId: uuidv4(),
    questionType: QUESTION_TYPES.MULTIPLE_CHOICE,
    questionText: '',
    multipleChoiceOptions: [
      { value: 'A', text: '', isCorrect: false, feedback: '' },
      { value: 'B', text: '', isCorrect: false, feedback: '' },
      { value: 'C', text: '', isCorrect: false, feedback: '' }
    ],
    hintText: '',
    imageFile: null,
    imagePreview: null,
    mediaUrl: null,
    correctAnswer: '',
    hints: [{ text: '', additionalText: '', imageFile: null, imagePreview: null, imageUrl: null }]
  });
  
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const navigate = useNavigate();
  
  // Add state for editing an existing question
  const [isEditing, setIsEditing] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  
  // Add cleanup for any pending operations
  useEffect(() => {
    // Flag to track if component is mounted
    let isMounted = true;
    
    // Return cleanup function
    return () => {
      isMounted = false;
      
      // Clean up any lingering DOM elements to avoid removeChild errors
      try {
        // Handle dynamic style sheets
        const styleSheets = document.querySelectorAll('style[data-generated="true"]');
        styleSheets.forEach(sheet => {
          if (sheet && sheet.parentNode) {
            try {
              sheet.parentNode.removeChild(sheet);
            } catch (e) {
              console.warn('Failed to remove style sheet:', e);
            }
          }
        });
        
        // Handle any React Portal elements that might be causing issues
        const portalElements = document.querySelectorAll('[data-reactroot]');
        portalElements.forEach(element => {
          // Only remove elements that are not currently in the DOM tree
          if (element && !document.body.contains(element) && element.parentNode) {
            try {
              element.parentNode.removeChild(element);
            } catch (e) {
              console.warn('Failed to remove portal element:', e);
            }
          }
        });
      } catch (error) {
        console.warn('Error during component cleanup:', error);
      }
    };
  }, []);
  
  // Load story data if editing an existing story
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // If editing an existing story
        if (storyId) {
          const response = await getGameStory(storyId);
          if (response.success) {
            setStory(response.data);
          } else {
            setError('ストーリーデータの読み込みに失敗しました');
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('データの読み込みに失敗しました。もう一度お試しください。');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [storyId]);
  
  const handleStoryChange = (field, value) => {
    console.log(`Updating story field "${field}" with value:`, value);
    setStory(prev => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
  };
  
  const handleNewQuestionChange = (field, value) => {
    if (field === 'questionType') {
      // When changing question type, reset type-specific fields to avoid invalid state
      const baseQuestion = {
        questionId: newQuestion.questionId,
        questionType: value,
        questionText: newQuestion.questionText,
        mediaUrl: newQuestion.mediaUrl,
        generalFeedback: newQuestion.generalFeedback,
        hintText: newQuestion.hintText,
        points: newQuestion.points,
        imageFile: null,
        imagePreview: null,
        hints: []
      };
      
      // Add type-specific default fields
      if (value === QUESTION_TYPES.MULTIPLE_CHOICE) {
        setNewQuestion({
          ...baseQuestion,
          multipleChoiceOptions: [
            { value: 'A', text: '', isCorrect: false, feedback: '' },
            { value: 'B', text: '', isCorrect: false, feedback: '' },
            { value: 'C', text: '', isCorrect: false, feedback: '' }
          ]
        });
      } else if (value === QUESTION_TYPES.FILE_UPLOAD) {
        setNewQuestion({
          ...baseQuestion,
          acceptedFileTypes: '',
          mediaUrl: '',
          multipleChoiceOptions: []
        });
      } else if (value === QUESTION_TYPES.NO_ANSWER) {
        setNewQuestion({
          ...baseQuestion,
          feedbackText: '情報を確認しました。次に進みましょう。',
          correctAnswer: '',
          multipleChoiceOptions: [],
          hints: [{ text: '', additionalText: '', imageFile: null, imagePreview: null, imageUrl: null }],
          mediaUrl: ''
        });
      } else if (value === QUESTION_TYPES.TEXT) {
        setNewQuestion({
          ...baseQuestion,
          questionText: newQuestion.questionText,
          multipleChoiceOptions: [],
          acceptedFileTypes: '',
          mediaUrl: '',
          correctAnswer: '',
          hints: []
        });
      } else {
        setNewQuestion({
          ...baseQuestion,
          multipleChoiceOptions: []
        });
      }
    } else {
      setNewQuestion(prev => ({ ...prev, [field]: value }));
    }
  };
  
  const handleOptionChange = (index, field, value) => {
    setNewQuestion(prev => {
      const updatedOptions = [...prev.multipleChoiceOptions];
      updatedOptions[index] = { ...updatedOptions[index], [field]: value };
      return { ...prev, multipleChoiceOptions: updatedOptions };
    });
  };
  
  const addOption = () => {
    setNewQuestion(prev => ({
      ...prev,
      multipleChoiceOptions: [
        ...prev.multipleChoiceOptions,
        { 
          value: String.fromCharCode(65 + prev.multipleChoiceOptions.length), 
          text: '', 
          isCorrect: false, 
          feedback: '' 
        }
      ]
    }));
  };
  
  const removeOption = (index) => {
    setNewQuestion(prev => {
      const options = [...prev.multipleChoiceOptions];
      options.splice(index, 1);
      // Re-label options
      return {
        ...prev,
        multipleChoiceOptions: options.map((opt, i) => ({
          ...opt,
          value: String.fromCharCode(65 + i)
        }))
      };
    });
  };
  
  // Add function to handle hint changes
  const handleHintChange = (index, field, value) => {
    setNewQuestion(prev => {
      const updatedHints = [...prev.hints];
      updatedHints[index] = { ...updatedHints[index], [field]: value };
      return { ...prev, hints: updatedHints };
    });
  };

  // Add function to handle hint image changes
  const handleHintImageChange = (index, file) => {
    if (!file) return;
    
    // Validate if it's an image
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onload = (event) => {
      const imagePreview = event.target.result;
      handleHintChange(index, 'imagePreview', imagePreview);
      handleHintChange(index, 'imageFile', file);
      handleHintChange(index, 'imageUrl', imagePreview);
    };
    reader.readAsDataURL(file);
  };

  // Add function to add new hint
  const addHint = () => {
    setNewQuestion(prev => ({
      ...prev,
      hints: [...prev.hints, { 
        text: '', 
        additionalText: '', 
        imageFile: null, 
        imagePreview: null, 
        imageUrl: null 
      }]
    }));
  };

  // Add function to remove hint
  const removeHint = (index) => {
    setNewQuestion(prev => {
      const hints = [...prev.hints];
      hints.splice(index, 1);
      return { ...prev, hints };
    });
  };
  
  // Add function to start editing an existing question
  const startEditingQuestion = (questionId) => {
    const questionToEdit = story.questionsAnswers.find(q => q.questionId === questionId);
    
    if (questionToEdit) {
      console.log("Question being edited (raw data):", JSON.stringify(questionToEdit)); // Debug log
      
      // Set up image preview for NO_ANSWER questions with mediaUrl
      let imagePreview = null;
      if (questionToEdit.questionType === QUESTION_TYPES.NO_ANSWER && questionToEdit.mediaUrl) {
        imagePreview = questionToEdit.mediaUrl;
      }
      
      // Format hints data
      const formattedHints = questionToEdit.hints || [];
      // Make sure each hint has all required fields
      const processedHints = formattedHints.map(hint => {
        console.log("Processing hint:", JSON.stringify(hint)); // Debug log
        return {
          text: hint.text || '',
          additionalText: hint.additionalText || '',
          imageFile: null,
          imagePreview: hint.imageUrl || null,
          imageUrl: hint.imageUrl || null
        };
      });
      
      // Create a properly structured question object
      const editedQuestion = {
        ...questionToEdit,
        imagePreview,
        imageFile: null,
        points: questionToEdit.points || 1,
        hints: processedHints.length > 0 ? processedHints : [{ 
          text: '', 
          additionalText: '', 
          imageFile: null, 
          imagePreview: null, 
          imageUrl: null 
        }],
        correctAnswer: questionToEdit.correctAnswer || '',
        multipleChoiceOptions: questionToEdit.multipleChoiceOptions ? 
          [...questionToEdit.multipleChoiceOptions] : 
          [
            { value: 'A', text: '', isCorrect: false, feedback: '' },
            { value: 'B', text: '', isCorrect: false, feedback: '' },
            { value: 'C', text: '', isCorrect: false, feedback: '' }
          ]
      };
      
      console.log("Processed edited question:", JSON.stringify({
        questionId: editedQuestion.questionId,
        questionType: editedQuestion.questionType,
        correctAnswer: editedQuestion.correctAnswer,
        hints: editedQuestion.hints.map(h => ({
          text: h.text ? h.text.substring(0, 20) + '...' : '',
          additionalText: h.additionalText ? h.additionalText.substring(0, 20) + '...' : '',
          hasImage: !!h.imageUrl
        }))
      })); // Debug log
      
      // Set the newQuestion state to the question being edited
      setNewQuestion(editedQuestion);
      setIsEditing(true);
      setEditingQuestionId(questionId);
      
      setActiveTab('questions');
      setTimeout(() => {
        const editForm = document.getElementById('question-form');
        if (editForm) {
          editForm.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      setError('編集する質問が見つかりませんでした');
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate if it's an image
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onload = (event) => {
      setNewQuestion(prev => ({
        ...prev,
        imageFile: file,
        imagePreview: event.target.result
      }));
    };
    reader.readAsDataURL(file);
  };
  
  // Add a diagnostic function to compare fields
  const compareQuestionFields = (originalQuestion, editedQuestion) => {
    console.log('=== QUESTION FIELD COMPARISON ===');
    
    // Create a set of all property names from both objects
    const allProps = new Set([
      ...Object.keys(originalQuestion),
      ...Object.keys(editedQuestion)
    ]);
    
    // Check each property
    allProps.forEach(prop => {
      const originalValue = originalQuestion[prop];
      const editedValue = editedQuestion[prop];
      
      if (prop === 'hints') {
        console.log('Original hints:', originalValue);
        console.log('Edited hints:', editedValue);
      } else if (prop === 'correctAnswer') {
        console.log('Original correctAnswer:', originalValue);
        console.log('Edited correctAnswer:', editedValue);
      } else if (originalValue !== editedValue) {
        console.log(`Property '${prop}' changed: ${originalValue} -> ${editedValue}`);
      }
    });
    
    console.log('=== END COMPARISON ===');
  };

  // Function to ensure correctAnswer is properly set
  const ensureCorrectAnswer = (question) => {
    if (question.questionType === QUESTION_TYPES.NO_ANSWER) {
      // Make sure correctAnswer exists and is a string
      if (question.correctAnswer === undefined || question.correctAnswer === null) {
        question.correctAnswer = '';
      }
      // Force to string if it's not already
      question.correctAnswer = String(question.correctAnswer);
      
      console.log('Ensured correctAnswer is set:', question.correctAnswer);
    }
    return question;
  };

  // Function to ensure hint fields are properly set
  const ensureHintFields = (question) => {
    if (question.hints && Array.isArray(question.hints)) {
      question.hints = question.hints.map(hint => ({
        text: hint.text || '',
        additionalText: hint.additionalText || '',
        imageFile: hint.imageFile || null,
        imagePreview: hint.imagePreview || hint.imageUrl || null,
        imageUrl: hint.imageUrl || hint.imagePreview || null
      }));
      
      console.log('Processed hints:', question.hints);
    }
    return question;
  };

  // Update the add question function to handle both adding and updating
  const addQuestion = () => {
    try {
      // Validate required fields - check questionText for all question types
      if (!newQuestion.questionText) {
        setError('質問文は必須です');
        return;
      }

      // For NO_ANSWER questions, validate correctAnswer
      if (newQuestion.questionType === QUESTION_TYPES.NO_ANSWER && !newQuestion.correctAnswer) {
        setError('正解は必須です');
        return;
      }

      // For MULTIPLE_CHOICE questions, validate options
      if (newQuestion.questionType === QUESTION_TYPES.MULTIPLE_CHOICE) {
        // Check if at least one option is marked as correct
        if (!newQuestion.multipleChoiceOptions.some(opt => opt.isCorrect)) {
          setError('少なくとも1つの正解を選択してください');
          return;
        }
        
        // Check if all options have text
        if (newQuestion.multipleChoiceOptions.some(opt => !opt.text.trim())) {
          setError('すべての選択肢にテキストを入力してください');
          return;
        }
      }

      // Create a copy of the question to save
      const questionToSave = {
        ...newQuestion,
        questionId: isEditing ? editingQuestionId : uuidv4(),
        points: 1  // Set default points to 1
      };

      // For NO_ANSWER type, process the uploaded image if it exists
      if (questionToSave.questionType === QUESTION_TYPES.NO_ANSWER && questionToSave.imageFile) {
        // Store the image preview as the mediaUrl
        questionToSave.mediaUrl = questionToSave.imagePreview;
      }

      // Process hints images for all question types
      if (questionToSave.hints && questionToSave.hints.length > 0) {
        questionToSave.hints = questionToSave.hints.map(hint => {
          if (hint.imageFile) {
            hint.imageUrl = hint.imagePreview;
          }
          return hint;
        });

        // Filter out any empty hints
        questionToSave.hints = questionToSave.hints.filter(
          hint => hint.text || hint.additionalText || hint.imageUrl
        );
      }

      // Update or add the question
      if (isEditing) {
        // Update the story state with the updated questions array
        setStory(prev => {
          const updatedQuestions = prev.questionsAnswers.map(q =>
            q.questionId === editingQuestionId ? questionToSave : q
          );
          return { ...prev, questionsAnswers: updatedQuestions };
        });

        setSuccessMessage('質問が更新されました');

        // Exit editing mode
        setIsEditing(false);
        setEditingQuestionId(null);
      } else {
        // Add the new question to the story
        setStory(prev => ({
          ...prev,
          questionsAnswers: [...prev.questionsAnswers, questionToSave]
        }));

        setSuccessMessage('質問が追加されました');
      }

      // Reset the question form
      setNewQuestion({
        questionId: uuidv4(),
        questionType: QUESTION_TYPES.MULTIPLE_CHOICE,
        questionText: '',
        correctAnswer: '',
        multipleChoiceOptions: [
          { value: 'A', text: '', isCorrect: false, feedback: '' },
          { value: 'B', text: '', isCorrect: false, feedback: '' },
          { value: 'C', text: '', isCorrect: false, feedback: '' }
        ],
        imageFile: null,
        imagePreview: null,
        mediaUrl: null,
        hints: [{ text: '', additionalText: '', imageFile: null, imagePreview: null, imageUrl: null }]
      });

    } catch (error) {
      console.error('Error adding/updating question:', error);
      setError(`エラーが発生しました: ${error.message}`);
    }
  };
  
  // Add a function to cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditingQuestionId(null);
    
    setNewQuestion({
      questionId: uuidv4(),
      questionType: QUESTION_TYPES.MULTIPLE_CHOICE,
      questionText: '',
      multipleChoiceOptions: [
        { value: 'A', text: '', isCorrect: false, feedback: '' },
        { value: 'B', text: '', isCorrect: false, feedback: '' },
        { value: 'C', text: '', isCorrect: false, feedback: '' }
      ],
      correctAnswer: '',
      imageFile: null,
      imagePreview: null,
      mediaUrl: null,
      hints: [{ text: '', additionalText: '', imageFile: null, imagePreview: null, imageUrl: null }]
    });
  };
  
  const removeQuestion = (questionId) => {
    setStory(prev => ({
      ...prev,
      questionsAnswers: prev.questionsAnswers.filter(q => q.questionId !== questionId)
    }));
  };
  
  const updateQuestion = (updatedQuestionType = null) => {
    const questionType = updatedQuestionType || newQuestion.questionType;
    
    // Create a base question with common fields
    const baseQuestion = {
      ...newQuestion,
      questionType
    };
    
    // For NO_ANSWER type, process the uploaded image if it exists
    if (questionType === QUESTION_TYPES.NO_ANSWER && baseQuestion.imageFile) {
      // Store the image preview as the mediaUrl
      // In a real implementation, you would upload the file to a server and store the URL
      baseQuestion.mediaUrl = baseQuestion.imagePreview;
    }
    
    // Reset type-specific fields
    if (questionType === QUESTION_TYPES.MULTIPLE_CHOICE) {
      baseQuestion.acceptedFileTypes = '';
      if (!baseQuestion.multipleChoiceOptions || baseQuestion.multipleChoiceOptions.length === 0) {
        baseQuestion.multipleChoiceOptions = [
          { value: 'A', text: '', isCorrect: false, feedback: '' },
          { value: 'B', text: '', isCorrect: false, feedback: '' },
          { value: 'C', text: '', isCorrect: false, feedback: '' }
        ];
      }
    } else if (questionType === QUESTION_TYPES.FILE_UPLOAD) {
      baseQuestion.multipleChoiceOptions = [];
      baseQuestion.mediaUrl = '';
      baseQuestion.imageFile = null;
      baseQuestion.imagePreview = null;
    } else if (questionType === QUESTION_TYPES.NO_ANSWER) {
      baseQuestion.multipleChoiceOptions = [];
      baseQuestion.acceptedFileTypes = '';
      // Don't clear mediaUrl for NO_ANSWER if it was set from imagePreview above
      if (!baseQuestion.imageFile && !baseQuestion.imagePreview) {
        baseQuestion.mediaUrl = '';
      }
    } else if (questionType === QUESTION_TYPES.TEXT) {
      baseQuestion.multipleChoiceOptions = [];
      baseQuestion.acceptedFileTypes = '';
    } else {
      baseQuestion.multipleChoiceOptions = [];
      baseQuestion.acceptedFileTypes = '';
    }
    
    // Update the question in the questions array
    const updatedQuestions = story.questionsAnswers.map(q => 
      q.questionId === baseQuestion.questionId ? baseQuestion : q
    );
    
    setStory(prev => ({
      ...prev,
      questionsAnswers: updatedQuestions
    }));
    setSuccessMessage('質問が更新されました');
    cancelEditing();
  };
  
  const saveStory = async () => {
    // Set loading state first 
    setIsSaving(true);
    
    // Clear any existing messages
    setError('');
    setSuccessMessage('');
    
    try {
      console.log('Starting save story process...');
      
      // Check if admin token is present
      const adminToken = localStorage.getItem('adminAuth');
      if (!adminToken) {
        console.error('Admin authentication token not found');
        setError('管理者認証が見つかりません。再度ログインしてください。');
        setIsSaving(false);
        return;
      }
      
      // Create a copy of the story data to avoid modifying state directly
      const storyData = JSON.parse(JSON.stringify(story));
      
      // Validate required fields
      if (!storyData.title) {
        setError('タイトルは必須です');
        setIsSaving(false);
        console.log('Save failed: Title is required');
        return;
      }
      
      if (storyData.questionsAnswers.length === 0) {
        setError('少なくとも1つの質問を追加してください');
        setIsSaving(false);
        console.log('Save failed: At least one question is required');
        return;
      }
      
      // IMPORTANT: Remove all unnecessary fields before saving
      console.log('Pre-processing all questions before saving...');
      storyData.questionsAnswers = storyData.questionsAnswers.map((question, qIndex) => {
        // Create a clean question object with only the fields needed by the model
        const cleanQuestion = {
          questionId: question.questionId,
          questionType: question.questionType,
          questionText: question.questionText || `Question ${qIndex + 1}`, // Ensure questionText is never empty
          points: question.points || 1
        };
        
        // Add mediaUrl if it exists and question type is not TEXT
        if (question.mediaUrl && question.questionType !== QUESTION_TYPES.TEXT) {
          cleanQuestion.mediaUrl = question.mediaUrl;
        }
        
        // Handle question type specific fields
        if (question.questionType === QUESTION_TYPES.MULTIPLE_CHOICE && question.multipleChoiceOptions) {
          cleanQuestion.multipleChoiceOptions = question.multipleChoiceOptions.map(opt => ({
            value: opt.value,
            text: opt.text,
            isCorrect: opt.isCorrect,
            feedback: opt.feedback
          }));
        }
        
        // Always include correctAnswer for NO_ANSWER questions only
        if (question.questionType === QUESTION_TYPES.NO_ANSWER) {
          cleanQuestion.correctAnswer = question.correctAnswer || '';
          console.log(`Question ${qIndex} correctAnswer:`, cleanQuestion.correctAnswer);
        }
        
        // Process hints if they exist and question type is not TEXT
        if (question.hints && Array.isArray(question.hints) && question.questionType !== QUESTION_TYPES.TEXT) {
          cleanQuestion.hints = question.hints
            .filter(hint => hint.text || hint.additionalText || hint.imageUrl)
            .map(hint => ({
              text: hint.text || '',
              additionalText: hint.additionalText || '',
              imageUrl: hint.imageUrl || null
            }));
          
          console.log(`Processed ${cleanQuestion.hints.length} hints for question ${qIndex}`);
        } else {
          cleanQuestion.hints = [];
        }
        
        return cleanQuestion;
      });
      
      // Add debug output for final data to be saved
      console.log('Saving story with:', {
        storyId: storyId || 'New story',
        title: storyData.title,
        questionCount: storyData.questionsAnswers.length,
        questionTypes: storyData.questionsAnswers.map(q => q.questionType),
        hasNoAnswerQuestions: storyData.questionsAnswers.some(q => q.questionType === QUESTION_TYPES.NO_ANSWER),
        correctAnswerSample: storyData.questionsAnswers
          .filter(q => q.questionType === QUESTION_TYPES.NO_ANSWER)
          .map(q => ({ id: q.questionId, answer: q.correctAnswer })),
        hintsSample: storyData.questionsAnswers
          .filter(q => q.hints && q.hints.length > 0)
          .map(q => ({ 
            id: q.questionId, 
            hintCount: q.hints.length,
            hasAdditionalText: q.hints.some(h => h.additionalText && h.additionalText.trim().length > 0)
          }))
      });
      
      // Send request to backend
      let response;
      try {
        response = storyId
          ? await updateGameStory(storyId, storyData)
          : await createGameStory(storyData);
        console.log('API response received:', response);
      } catch (apiError) {
        console.error('API error:', apiError);
        throw new Error(apiError.message || 'API request failed');
      }
      
      if (response?.success) {
        console.log('Story saved successfully:', response.data);
        
        // Handle success - wait for state update to complete before navigation
        if (!storyId) {
          // If creating a new story, first set success message
          setSuccessMessage('ストーリーが保存されました');
          
          // Wait for state updates to complete before navigation
          setTimeout(() => {
            console.log('Redirecting to edit page with new storyId:', response.data.storyId);
            navigate(`/admin/game-editor/${response.data.storyId}`);
          }, 100);
        } else {
          // Refresh data - safely update state
          console.log('Updating story data with server response');
          const responseData = response.data;
          
          // Update state with the response data
          setStory(responseData);
          
          // Set success message after updating story
          setSuccessMessage('ストーリーが保存されました');
        }
      } else {
        console.error('Save failed with error from API:', response?.message);
        setError(response?.message || 'ストーリーの保存に失敗しました');
      }
    } catch (err) {
      console.error('Error saving story:', err);
      console.error('Error details:', err.stack);
      setError(`ストーリーの保存に失敗しました: ${err.message || '不明なエラー'}`);
    } finally {
      // Ensure we properly reset loading state
      setIsSaving(false);
      console.log('Save process completed');
    }
  };
  
  const generateQRCode = async () => {
    if (!storyId) {
      setError('Please save the story first');
      return;
    }
    
    try {
      setIsLoading(true);
      const baseUrl = window.location.origin;
      const storyUrl = `${baseUrl}/story/${storyId}`;
      setQrCodeUrl(storyUrl);
      
      // Generate QR code on server
      await generateStoryQRCode(storyId, { url: storyUrl });
      setShowQrCode(true);
    } catch (err) {
      console.error('Error generating QR code:', err);
      // Check for validation errors in the error message
      if (err.message && err.message.includes('validation failed')) {
        setError('データの検証に失敗しました。質問の詳細をすべて入力して保存してください。');
      } else {
        setError(`QRコードの生成に失敗しました: ${err.message || '不明なエラー'}`);
      }
      
      // Still show QR code modal with local URL even if server-side generation failed
      const baseUrl = window.location.origin;
      const storyUrl = `${baseUrl}/story/${storyId}`;
      setQrCodeUrl(storyUrl);
      setShowQrCode(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const closeQRCode = () => {
    setShowQrCode(false);
  };
  
  // If still loading
  if (isLoading && !story) {
    return <LoadingState message="ストーリーを読み込み中..." />;
  }
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ゲームエディタ</h1>
        <p className="text-gray-600 mt-1">ゲームストーリーを作成または編集する</p>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 font-medium">エラーが発生しました</p>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => {
                  console.log('Current story state:', story);
                  alert('詳細はブラウザのコンソールを確認してください。開発者に以下のエラーを報告してください: ' + error);
                }}
                className="text-xs text-red-800 underline mt-1"
              >
                トラブルシューティング情報を表示
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="mb-4 flex justify-between">
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'details'
                ? 'bg-indigo-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            詳細
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('questions')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'questions'
                ? 'bg-indigo-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            質問
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('preview')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              activeTab === 'preview'
                ? 'bg-indigo-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            プレビュー
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={saveStory}
            disabled={isSaving}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                保存中...
              </>
            ) : (
              '保存'
            )}
          </button>
          {storyId && (
            <button
              type="button"
              onClick={generateQRCode}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  QRコード生成中...
                </>
              ) : (
                'QRコード生成'
              )}
            </button>
          )}
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow">
        {activeTab === 'details' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">ストーリー詳細</h3>
            
            <div className="p-6 bg-white rounded-lg shadow-md">
              <h3 className="text-lg font-medium text-gray-900 mb-4">ストーリー詳細</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">タイトル <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={story.title}
                    onChange={(e) => handleStoryChange('title', e.target.value)}
                    className="mt-1 p-2 w-full border border-gray-300 rounded-md"
                    placeholder="ストーリーのタイトル"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">説明</label>
                  <textarea
                    value={story.description}
                    onChange={(e) => handleStoryChange('description', e.target.value)}
                    className="mt-1 p-2 w-full border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="ストーリーの簡単な説明"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'questions' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">質問</h3>
            
            {story.questionsAnswers.length > 0 && (
              <div className="space-y-4 mb-8">
                <h4 className="font-medium">既存の質問</h4>
                {story.questionsAnswers.map((question, index) => (
                  <div key={`question-${question.questionId || index}`} className="p-4 border border-gray-300 rounded-md">
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-medium">
                        Question {index + 1}: 
                        {question.questionType === QUESTION_TYPES.MULTIPLE_CHOICE 
                          ? (question.questionText?.substring(0, 30) || "Multiple choice question") + "..."
                          : question.questionText?.substring(0, 30) + "..."}
                      </h5>
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={() => startEditingQuestion(question.questionId)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removeQuestion(question.questionId)}
                          className="text-red-600 hover:text-red-800"
                        >
                          delete
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      Type: {QUESTION_TYPE_LABELS[question.questionType] || question.questionType}
                    </div>
                    
                    {/* Show image thumbnail and hints for NO_ANSWER questions with mediaUrl */}
                    {question.questionType === QUESTION_TYPES.NO_ANSWER && (
                      <div className="mt-2 space-y-3">
                        {question.mediaUrl && (
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Image:</div>
                            <div className="relative group">
                              <img 
                                src={question.mediaUrl} 
                                alt="Question image" 
                                className="h-16 w-auto object-contain rounded-md border border-gray-200 cursor-pointer"
                                onClick={() => {
                                  // Create full-screen preview
                                  const modal = document.createElement('div');
                                  modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50';
                                  modal.onclick = () => document.body.removeChild(modal);
                                  
                                  const img = document.createElement('img');
                                  img.src = question.mediaUrl;
                                  img.className = 'max-h-[90vh] max-w-[90vw] object-contain';
                                  img.onclick = e => e.stopPropagation();
                                  
                                  modal.appendChild(img);
                                  document.body.appendChild(modal);
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 flex items-center justify-center transition-opacity duration-200 rounded-md">
                                <span className="text-transparent group-hover:text-white text-xs font-medium">
                                  Click to preview
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Show hints preview */}
                        {question.hints && question.hints.length > 0 && (
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Hints ({question.hints.length}):</div>
                            <div className="space-y-2">
                              {question.hints.map((hint, hintIndex) => (
                                <div key={hintIndex} className="bg-gray-50 rounded-md p-2 text-sm">
                                  <p className="text-gray-700 line-clamp-1">{hint.text}</p>
                                  {(hint.imagePreview || hint.imageUrl) && (
                                    <div className="mt-1">
                                      <img 
                                        src={hint.imagePreview || hint.imageUrl} 
                                        alt={`Hint ${hintIndex + 1} image`}
                                        className="h-12 w-auto object-contain rounded"
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div id="question-form" className="p-4 border border-gray-300 rounded-md space-y-4">
              <h4 className="font-medium">{isEditing ? 'Edit Question' : 'Add New Question'}</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Question Type</label>
                  <select
                    value={newQuestion.questionType}
                    onChange={(e) => handleNewQuestionChange('questionType', e.target.value)}
                    className="mt-1 p-2 w-full border border-gray-300 rounded-md"
                  >
                    <option value={QUESTION_TYPES.MULTIPLE_CHOICE}>Multiple Choice Questions</option>
                    <option value={QUESTION_TYPES.FILE_UPLOAD}>File Upload</option>
                    <option value={QUESTION_TYPES.NO_ANSWER}>Information Only</option>
                    <option value={QUESTION_TYPES.TEXT}>Text Input</option>
                  </select>
                </div>
              </div>
              
              {newQuestion.questionType !== QUESTION_TYPES.MULTIPLE_CHOICE && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Question <span className="text-red-500">*</span></label>
                  <textarea
                    value={newQuestion.questionText}
                    onChange={(e) => handleNewQuestionChange('questionText', e.target.value)}
                    className="mt-1 p-2 w-full border border-gray-300 rounded-md"
                    rows="3"
                  ></textarea>
                </div>
              )}
              
              {newQuestion.questionType !== QUESTION_TYPES.MULTIPLE_CHOICE && 
                newQuestion.questionType !== QUESTION_TYPES.FILE_UPLOAD &&
                newQuestion.questionType !== QUESTION_TYPES.NO_ANSWER &&
                newQuestion.questionType !== QUESTION_TYPES.TEXT && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Media URL (optional)</label>
                  <input
                    type="text"
                    value={newQuestion.mediaUrl}
                    onChange={(e) => handleNewQuestionChange('mediaUrl', e.target.value)}
                    className="mt-1 p-2 w-full border border-gray-300 rounded-md"
                    placeholder="URL of image or audio file"
                  />
                </div>
              )}
              
              {newQuestion.questionType === QUESTION_TYPES.MULTIPLE_CHOICE && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Choices</label>
                    <button
                      type="button"
                      onClick={addOption}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Add a choice
                    </button>
                  </div>
                  
                  {newQuestion.multipleChoiceOptions.map((option, index) => (
                    <div key={`option-${option.value}-${index}`} className="mb-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span className="mr-2 text-sm font-medium text-gray-700">
                            Option {option.value}
                          </span>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={option.isCorrect}
                              onChange={(e) => handleOptionChange(index, 'isCorrect', e.target.checked)}
                              className="mr-2 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              id={`correct-${index}`}
                            />
                            <label htmlFor={`correct-${index}`} className="text-sm text-gray-700">
                              correct answer
                            </label>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          disabled={newQuestion.multipleChoiceOptions.length <= 2}
                        >
                          delete
                        </button>
                      </div>
                      
                      <div className="mt-2 mb-2">
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                          className="p-2 w-full border border-gray-300 rounded-md"
                          placeholder="Choice text"
                        />
                      </div>
                      
                      <div>
                        <input
                          type="text"
                          value={option.feedback || ""}
                          onChange={(e) => handleOptionChange(index, 'feedback', e.target.value)}
                          className="p-2 w-full border border-gray-300 rounded-md"
                          placeholder="Feedback if you choose this option (optional)"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {newQuestion.questionType === QUESTION_TYPES.NO_ANSWER && (
                <>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-lg">Correct Answer</span>
                      <span className="ml-1 text-red-500">*</span>
                      <span className="ml-2 text-sm text-gray-500">
                        (Required for verifying user answers)
                      </span>
                    </label>
                    <div className="bg-yellow-50 p-3 rounded-md border border-yellow-300 mb-2">
                      <div className="flex items-center text-amber-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Important: Users must enter this exact answer to proceed</span>
                      </div>
                    </div>
                    <div className="bg-yellow-50 p-2 rounded-md border border-yellow-300">
                      <input
                        type="text"
                        value={newQuestion.correctAnswer || ''}
                        onChange={(e) => handleNewQuestionChange('correctAnswer', e.target.value)}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-3 bg-white"
                        placeholder="Enter the correct answer..."
                        required
                      />
                      <p className="mt-1 text-sm text-amber-800 font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        This field is required and will be used to validate user answers.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload Image (optional)</label>
                    
                    {newQuestion.imagePreview ? (
                      <div className="relative border-2 border-indigo-200 rounded-lg p-2 bg-gray-50">
                        <div className="flex flex-col items-center">
                          <img 
                            src={newQuestion.imagePreview} 
                            alt="Preview" 
                            className="max-h-64 max-w-full object-contain rounded-md shadow-sm"
                          />
                          <div className="mt-3 w-full flex justify-between items-center">
                            <span className="text-xs text-gray-500 truncate max-w-xs">
                              {newQuestion.imageFile?.name || "Uploaded image"}
                            </span>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const fileInput = document.getElementById('image-upload-input');
                                  if (fileInput) fileInput.click();
                                }}
                                className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
                                </svg>
                                Change
                              </button>
                              <button
                                type="button"
                                onClick={() => setNewQuestion(prev => ({ 
                                  ...prev, 
                                  imageFile: null, 
                                  imagePreview: null 
                                }))}
                                className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col justify-center items-center hover:border-indigo-300 transition-colors duration-300"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add('border-indigo-500');
                          e.currentTarget.classList.add('bg-indigo-50');
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-indigo-500');
                          e.currentTarget.classList.remove('bg-indigo-50');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-indigo-500');
                          e.currentTarget.classList.remove('bg-indigo-50');
                          
                          const files = e.dataTransfer.files;
                          if (files && files.length > 0) {
                            const file = files[0];
                            if (!file.type.match('image.*')) {
                              setError('Please select an image file');
                              return;
                            }
                            
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setNewQuestion(prev => ({
                                ...prev,
                                imageFile: file,
                                imagePreview: event.target.result
                              }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      >
                        <input
                          id="image-upload-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageChange}
                        />
                        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="mt-2 text-sm text-gray-600">Drag and drop an image here, or</p>
                        <button
                          type="button"
                          onClick={() => {
                            const fileInput = document.getElementById('image-upload-input');
                            if (fileInput) fileInput.click();
                          }}
                          className="mt-2 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
                          </svg>
                          Browse files
                        </button>
                        <p className="mt-2 text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Hints Section */}
                  <div className="mt-6 border-t-2 border-gray-200 pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <label className="block text-lg font-medium text-gray-700">
                        Hints
                        <span className="ml-2 text-sm text-gray-500">(Users can view these for help)</span>
                      </label>
                      <button
                        type="button"
                        onClick={addHint}
                        className="inline-flex items-center px-3 py-1 border border-indigo-300 shadow-sm text-sm leading-4 font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Hint
                      </button>
                    </div>

                    <div className="space-y-4">
                      {newQuestion.hints.map((hint, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-3 pb-2 border-b border-gray-100">
                            <h6 className="text-md font-medium text-indigo-700">Hint {index + 1}</h6>
                            <button
                              type="button"
                              onClick={() => removeHint(index)}
                              className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded-full"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Hint Text</label>
                              <textarea
                                value={hint.text}
                                onChange={(e) => handleHintChange(index, 'text', e.target.value)}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                rows="2"
                                placeholder="Enter hint text..."
                              />
                              <p className="mt-1 text-xs text-gray-500">This text appears before the hint image.</p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Additional Hint Text
                                <span className="ml-2 text-sm text-blue-500">(Important)</span>
                              </label>
                              <div className="bg-blue-50 p-2 rounded-md">
                                <textarea
                                  value={hint.additionalText || ''}
                                  onChange={(e) => handleHintChange(index, 'additionalText', e.target.value)}
                                  className="w-full rounded-md border-blue-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white"
                                  rows="2"
                                  placeholder="Enter additional hint text that will appear after the image..."
                                />
                                <p className="mt-1 text-xs text-blue-700">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  This text will appear after the hint image and provides additional context.
                                </p>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Hint Image (optional)</label>
                              {hint.imagePreview ? (
                                <div className="relative border border-gray-200 rounded-lg p-2">
                                  <img 
                                    src={hint.imagePreview} 
                                    alt={`Hint ${index + 1}`} 
                                    className="max-h-48 w-auto object-contain rounded-md"
                                  />
                                  <div className="absolute top-2 right-2 flex space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const fileInput = document.getElementById(`hint-image-${index}`);
                                        if (fileInput) fileInput.click();
                                      }}
                                      className="bg-white p-1 rounded-full shadow-sm hover:bg-gray-100"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleHintChange(index, 'imagePreview', null)}
                                      className="bg-white p-1 rounded-full shadow-sm hover:bg-gray-100"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-300 transition-colors duration-300 cursor-pointer"
                                  onClick={() => {
                                    const fileInput = document.getElementById(`hint-image-${index}`);
                                    if (fileInput) fileInput.click();
                                  }}
                                >
                                  <input
                                    id={`hint-image-${index}`}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleHintImageChange(index, e.target.files[0])}
                                  />
                                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <p className="mt-1 text-sm text-gray-500">Click to upload hint image</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={addQuestion}
                  className="mt-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {isEditing ? 'Update Question' : 'Add Question'}
                </button>
                
                {isEditing && (
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="mt-4 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'preview' && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-gray-900">ストーリープレビュー</h2>
            
            <div className="bg-gray-100 rounded-md p-4">
              <h3 className="font-medium text-lg">{story.title || 'タイトルなし'}</h3>
              <p className="text-gray-700 mt-2">{story.description || '説明なし'}</p>
              
              <div className="mt-4">
                <h4 className="font-medium">質問 ({story.questionsAnswers.length})</h4>
                {story.questionsAnswers.length > 0 ? (
                  <div className="space-y-3 mt-2">
                    {story.questionsAnswers.map((q, idx) => (
                      <div key={`question-${q.questionId || idx}`} className="p-3 bg-white rounded-md">
                        <h5 className="font-medium">
                          質問 {idx + 1}: 
                          {q.questionType === QUESTION_TYPES.MULTIPLE_CHOICE 
                            ? q.questionText || "Multiple choice question"
                            : q.questionText}
                        </h5>
                        <p className="text-gray-600 text-sm mt-1">
                          タイプ: {QUESTION_TYPE_LABELS[q.questionType] || q.questionType}
                        </p>
                        
                        {/* Display image for NO_ANSWER questions */}
                        {q.questionType === QUESTION_TYPES.NO_ANSWER && (
                          <div className="mt-3 space-y-4">
                            {q.mediaUrl && (
                              <div className="relative rounded-lg overflow-hidden border border-gray-200 inline-block">
                                <img 
                                  src={q.mediaUrl} 
                                  alt="Question image" 
                                  className="max-h-48 max-w-full object-contain"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Display text input field for TEXT questions */}
                        {q.questionType === QUESTION_TYPES.TEXT && (
                          <div className="mt-3">
                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                              <p className="text-sm text-gray-600">ユーザーはテキストを入力して回答します</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Display hints */}
                        {q.hints && q.hints.length > 0 && (
                          <div className="mt-4">
                            <h6 className="text-sm font-medium text-gray-700 mb-2">Hints:</h6>
                            <div className="space-y-3">
                              {q.hints.map((hint, hintIndex) => (
                                <div key={hintIndex} className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-sm text-gray-800">{hint.text}</p>
                                  {(hint.imagePreview || hint.imageUrl) && (
                                    <div className="mt-2">
                                      <img 
                                        src={hint.imagePreview || hint.imageUrl} 
                                        alt={`Hint ${hintIndex + 1} image`} 
                                        className="max-h-32 w-auto object-contain rounded-md"
                                      />
                                    </div>
                                  )}
                                  {hint.additionalText && (
                                    <p className="text-sm text-gray-800 mt-2">{hint.additionalText}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 mt-1">質問がありません</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {showQrCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">{story.title}のQRコード</h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={closeQRCode}
              >
                <span className="sr-only">閉じる</span>
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex flex-col items-center justify-center">
              <div className="bg-white p-4 rounded-lg shadow">
                <QRCodeSVG
                  id="story-qrcode"
                  value={qrCodeUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="mt-4 text-sm text-gray-500 break-all text-center">
                {qrCodeUrl}
              </p>
            </div>
            
            <div className="mt-6 flex flex-col space-y-2">
              <button
                onClick={() => {
                  const svg = document.getElementById('story-qrcode');
                  if (!svg) return;
                  
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  const img = new Image();
                  
                  img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    const pngFile = canvas.toDataURL('image/png');
                    
                    const downloadLink = document.createElement('a');
                    downloadLink.download = `story-${storyId}-qr.png`;
                    downloadLink.href = pngFile;
                    downloadLink.click();
                  };
                  
                  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                }}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
              >
                QRコードをダウンロード
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(qrCodeUrl);
                  setSuccessMessage('URLがクリップボードにコピーされました');
                }}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700"
              >
                URLをコピー
              </button>
              <button
                type="button"
                onClick={closeQRCode}
                className="w-full bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add a floating save button at the bottom right of the page */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          type="button"
          onClick={saveStory}
          disabled={isSaving}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              保存中...
            </>
          ) : (
            '保存する'
          )}
        </button>
      </div>
      </div>
    </div>
  );
};

export default GameEditor;